import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { ApiError } from "@/lib/api/client";
import { formatDate } from "@/lib/formatters";
import type { HaltHistoryItem } from "../api/deployments.api";
import { canRecomputeHaltGate, haltOverrideStamp } from "../lib/halt-history";

interface HaltHistoryPanelProps {
  items: HaltHistoryItem[];
  isPending: boolean;
  isSuccess: boolean;
  error: ApiError | null | undefined;
}

function formatCount(value: number | null): string {
  return value === null ? "—" : value.toLocaleString();
}

function formatRate(value: number | null): string {
  if (value === null) return "—";
  return `${Math.round(value * 100)}%`;
}

export function HaltHistoryPanel({ items, isPending, isSuccess, error }: HaltHistoryPanelProps) {
  const { t } = useTranslation();
  const isEmpty = isSuccess && !error && items.length === 0;

  return (
    <section className="mt-8 rounded-xl border border-border bg-surface p-5">
      <div className="flex items-center gap-2">
        <ShieldAlert size={16} className="text-muted" />
        <h2 className="text-lg font-semibold text-heading">{t("deployments.haltHistoryTitle")}</h2>
      </div>
      <p className="mt-1 text-xs text-muted">{t("deployments.haltHistorySignal")}</p>

      <ApiErrorBanner error={error} className="mt-4" />

      {isPending && !error ? (
        <p className="mt-4 text-sm text-muted">{t("common.loading")}</p>
      ) : null}

      {isEmpty ? (
        <p className="mt-4 text-sm text-muted">{t("deployments.haltHistoryEmpty")}</p>
      ) : null}

      {items.length > 0 ? (
        <ul className="mt-4 space-y-3">
          {items.map((item) => (
            <HaltHistoryRow
              key={`${item.kind}-${item.label}-${item.occurredAt}-${item.ordinal ?? "w"}`}
              item={item}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

const stampOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

function HaltHistoryRow({ item }: { item: HaltHistoryItem }) {
  const { t } = useTranslation();
  const recomputable = canRecomputeHaltGate(item);
  const kindLabel =
    item.kind === "warn"
      ? t("deployments.haltHistoryKindWarn")
      : t("deployments.haltHistoryKindHalt");
  const kindClass =
    item.kind === "warn"
      ? "rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning"
      : "rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger";
  const overrideAt = haltOverrideStamp(item);

  return (
    <li className="rounded-lg border border-border px-3 py-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className={kindClass}>{kindLabel}</span>
        <span className="min-w-0 break-all font-mono text-sm font-semibold text-heading">
          {item.label}
        </span>
        <span className="text-xs text-muted">{formatDate(item.occurredAt, stampOptions)}</span>
      </div>
      {recomputable ? (
        <p className="mt-2 text-sm text-heading">
          {t("deployments.haltHistoryCounts", {
            served: formatCount(item.servedDevices),
            failed: formatCount(item.failedDevices),
            rate: formatRate(item.thresholdFailureRate),
          })}
        </p>
      ) : (
        <p className="mt-2 text-sm text-muted">{t("deployments.haltHistoryUnrecomputable")}</p>
      )}
      {overrideAt ? (
        <p className="mt-1 text-xs text-muted">
          {t("deployments.haltHistoryOverridden", { time: formatDate(overrideAt, stampOptions) })}
        </p>
      ) : null}
    </li>
  );
}
